import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {FileUploaderService} from '../../services/file-uploader.service';
import {FuseDeleteDialogComponent} from '../delete-dialog/delete-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {DocumentsService} from '../../services/documents.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import {th} from 'date-fns/locale';

@Component({
    selector: 'file-uploader',
    templateUrl: './file-uploader.component.html',
    styleUrls: ['./file-uploader.component.scss'],
    standalone: false
})

export class FileUploaderComponent implements OnChanges {
  @Input() uploadedFiles;
  @Input() formElementId;
  @Input() hideUploadButton;
  @Input() type = 'elearning';
  @Output() loadedFiles = new EventEmitter();

  public files = [];

  file;
  progressBar = 0;
  startProgress = false;
  progressFail = false;
  progressDone = false;
  fileId;
  fileKey;
  chunkSize = 1024 * 1024 * 5;
  parts = [];
  activeConnections = {};
  threadsQuantity = Math.min( 5, 15);
  aborted = false;
  uploadedParts = [];
  progressCache = {};
  uploadedSize = 0;
  youtubeLink = '';
  youtubeTitle = '';
  showYoutubeForm = false;

  constructor(private fileUploaderService: FileUploaderService, public dialog: MatDialog,
              private documentService: DocumentsService, public toastr: ToastrService,
              private translateService: TranslateService, private spinner: NgxSpinnerService) {
  }

  ngOnChanges(changes) {
    if (this.uploadedFiles && this.uploadedFiles.length) {
      for (let item of this.uploadedFiles) {
        if (item.storageType !== 'youtube') {
          item.url = item.name.split('/')[ 1 ];
          item.type = item.name.substring(item.name.lastIndexOf('.') + 1 , item.name.length);
          item.name = item.name.split('|_|')[ 1 ];
        }
      }
    }
  }

  getFile(name) {
    return this.documentService.getVideoStream(name);
  }

  handleInputChangeDoc(event: any) {
    event.preventDefault();
    this.uploadFileFunc(event.target.files[0]);
  }

  addFromYoutube() {
    this.showYoutubeForm = true;
  }

  saveYoutube() {
    if (this.youtubeLink && this.youtubeTitle){
      let obj = {
        name: this.youtubeTitle,
        fileName: this.youtubeTitle,
        type: this.type,
        storageType: 'youtube',
        url: this.youtubeLink
      };
      this.files.push(obj);
      this.loadedFiles.emit(this.files);
      this.cancelYoutube();
    } else {
      let title = this.translateService.instant('failure');
      let msg = this.translateService.instant('fill_req_field');
      this.toastr.error(msg, title);
    }
  }
  cancelYoutube() {
    this.showYoutubeForm = false;
    this.youtubeLink = '';
    this.youtubeTitle = '';
  }

  uploadFileFunc(file) {
    this.file = file;
    let fileName = Date.now().toString() + '|_|' + file.name;
    this.fileUploaderService.initializeResponse({ name: fileName }).subscribe((res: any) => {
      if (res) {
        this.startProgress = true;
        this.fileId = res.fileId;
        this.fileKey = res.fileKey;
        const partCount = Math.ceil(file.size / this.chunkSize);
        const AWSMultipartFileDataInput = {
          uploadId: this.fileId,
          name: this.fileKey,
          partNumber: partCount,
        };
        this.fileUploaderService.uploadPart(AWSMultipartFileDataInput).subscribe((uploadPart: any) => {
          const newParts = uploadPart.parts;
          this.parts.push(...newParts);
          this.sendNext();
        });
      }
    });
  }
  sendNext() {
    const activeConnections = Object.keys(this.activeConnections).length;

    if (activeConnections >= this.threadsQuantity) {
      return;
    }
    if (!this.parts.length) {
      if (!activeConnections) {
        return this.complete();
      }
      return;
    }

    const part = this.parts.shift();
    if (this.file && part) {
      const sentSize = (part.PartNumber - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

      const sendChunkStarted = () => {
        return this.sendNext();
      };

      return this.sendChunk(chunk, part, sendChunkStarted).then(() => {
        return this.sendNext();
      }).catch((error) => {
        this.parts.push(part);
        return this.complete(error);
      });
    }
  }

  private sendCompleteRequest() {
    if (this.fileId && this.fileKey) {
      this.uploadedParts.sort((a, b) => {
        if (a.PartNumber > b.PartNumber) {
          return 1;
        }
        if (a.PartNumber < b.PartNumber) {
          return -1;
        }
        return 0;
      });
      const videoFinalizationMultiPartInput = {
        UploadId: this.fileId,
        name: this.fileKey,
        parts: this.uploadedParts,
      };

      this.fileUploaderService.completeMultiPart(videoFinalizationMultiPartInput).subscribe((res: any) => {
        const fileNameSplit = this.file.name.split('.');
        const contentType = fileNameSplit[fileNameSplit.length - 1];
        let obj = {
          name: res.Key,
          fileName: this.file.name,
          type: this.type,
          publicAccess: true,
          storageType: 'video',
          fileType: contentType,
          url: res.Key.split('/')[ 1 ]
        };
        this.files.push(obj);
        this.loadedFiles.emit(this.files);
        this.progressDone = true;
        setTimeout(() => {
          this.startProgress = false;
        }, 2000);
        this.onFinishFn(this.fileKey);
      });
    }
  }

  onProgressFn = (data?) => {
  }
  onFinishFn = (data?) => {
  }
  onErrorFn = (data?) => {
  }

  private complete(error?) {
    if (error && !this.aborted) {
      this.onErrorFn(error);
      return;
    }

    if (error) {
      this.onErrorFn(error);
      return;
    }

    try {
      return this.sendCompleteRequest();
    } catch (error) {
      this.onErrorFn(error);
    }
  }

  private sendChunk(chunk, part, sendChunkStarted) {
    return new Promise((resolve, reject) => {
      this.upload(chunk, part, sendChunkStarted).then((status) => {
        if (status !== 200) {
          reject(new Error('Failed chunk upload'));
          return;
        }

        resolve(true);
      }).catch((error) => {
        reject(error);
      });
    });
  }

  private upload(file, part, sendChunkStarted) {
    return new Promise((resolve, reject) => {
      if (this.fileId && this.fileKey) {
        const xhr = (this.activeConnections[part.PartNumber - 1] = new XMLHttpRequest());

        sendChunkStarted();

        const progressListener = this.handleProgress.bind(this, part.PartNumber - 1);

        xhr.upload.addEventListener('progress', progressListener);

        xhr.addEventListener('error', progressListener);
        xhr.addEventListener('abort', progressListener);
        xhr.addEventListener('loadend', progressListener);

        xhr.open('PUT', part.signedUrl);

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4 && xhr.status === 200) {
            const ETag = xhr.getResponseHeader('ETag');

            if (ETag) {
              const uploadedPart = {
                PartNumber: part.PartNumber,
                ETag: ETag.replace(/\"/, ''),
              };

              this.uploadedParts.push(uploadedPart);

              resolve(xhr.status);
              delete this.activeConnections[part.PartNumber - 1];
            }
          }
        };

        xhr.onerror = (error) => {
          reject(error);
          delete this.activeConnections[part.PartNumber - 1];
        };

        xhr.onabort = () => {
          reject(new Error('Upload canceled by user'));
          delete this.activeConnections[part.PartNumber - 1];
        };

        xhr.send(file);
      }
    });
  }

  public onProgress(onProgress) {
    this.onProgressFn = onProgress;
    return this;
  }

  public onFinish(onFinish) {
    this.onFinishFn = onFinish;
    return this;
  }

  private onError(onError) {
    this.onErrorFn = onError;
    return this;
  }

  private abort() {
    Object.keys(this.activeConnections)
      .map(Number)
      .forEach((id) => {
        this.activeConnections[id].abort();
      });

    this.aborted = true;
  }
  private handleProgress(part, event) {
    if (this.file) {
      if (event.type === 'progress' || event.type === 'error' || event.type === 'abort') {
        this.progressCache[part] = event.loaded;
      }

      if (event.type === 'uploaded') {
        this.uploadedSize += this.progressCache[part] || 0;
        delete this.progressCache[part];
      }

      const inProgress = Object.keys(this.progressCache).map(Number).reduce((memo, id) => (memo += this.progressCache[id]), 0);

      const sent = Math.min(this.uploadedSize + inProgress, this.file.size);

      const total = this.file.size;

      const percentage = Math.round((sent / total) * 100);

      this.progressBar = percentage;

      this.onProgressFn({
        sent: sent,
        total: total,
        percentage: percentage,
      });
    }
  }

  removeFile (e) {
    const index = this.files.indexOf(e);
    if (index > -1) {
      this.files.splice(index, 1);
    }
  }

  removeFileFromUpload (e) {
    // console.log(e);
    const dialogRef = this.dialog.open(FuseDeleteDialogComponent , { panelClass: 'unsave-form-dialog',
      width: '300px' ,
      data: {title: 'Delete' , message: 'deleteConfirm'}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const index = this.uploadedFiles.indexOf(e);
        if (index > -1) {
          this.uploadedFiles.splice(index , 1);
        }
        this.documentService.deleteDocument(e.id).subscribe((res) => {
          if (res) {

          }
        });
      }
    });

  }

}
